import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  Check,
  Database,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { useClientLevel } from '../../hooks/useClientLevel';
import baseapi from '../../services/baseapi';
import { CustomDropdown } from '../../components/CustomDropdown ';
import DBTypeIcon from '../../components/DBTypeIcon';

// Import self-fetching subcomponents & DBSidebar
import { DBSidebar, DBMobileTabsBar, DBMobileDrawer } from './components/DBSidebar';
import { OverviewView } from './components/OverviewView';
import { DatabasesView } from './components/DatabasesView';
import { StorageMaintenanceTab } from './components/StorageMaintenanceTab';
import { QueriesView } from './components/QueriesView';
import { UsersLoginsView } from './components/UsersLoginsView';
import { UserQueryStatisticsView } from './components/UserQueryStatisticsView';
import { SchemaView } from './components/SchemaView';
import { CallingServicesView } from './components/CallingServicesView';
import { ConfigurationView } from './components/ConfigurationView';
import { IndexesView } from './components/IndexesView';
import { OpenSearchIndicesView } from './components/OpenSearchIndicesView';
// import { MongoCollectionsView } from './components/MongoCollectionsView';
import { MongoCollectionsView2 } from './components/MongoCollectionsView2';
import { MongoWiredTigerView } from './components/MongoWiredTigerView';
import { MetricsTab } from './components/MetricsTab';
import { ConnectionsTab } from './components/ConnectionsTab';
import { HealthTab } from './components/HealthTab';
import { MSSQLBackupView } from './components/MSSQLBackupView';
import { MSSQLTempDBView } from './components/MSSQLTempDBView';
import { MSSQLMemoryClerksView } from './components/MSSQLMemoryClerksView';
import { MSSQLSchedulersView } from './components/MSSQLSchedulersView';
import { MySQLScheduledJobsView } from './components/MySQLScheduledJobsView';
import { MSSQLFileIOView } from './components/MSSQLFileIOView';
import { MySQLFileIOView } from './components/MySQLFileIOView';
import { MongoFileIOView } from './components/MongoFileIOView';
import { PostgresFileIOView } from './components/PostgresFileIOView';
import { ClickhouseFileIOView } from './components/ClickhouseFileIOView';
import { ClickhouseStorage } from './components/ClickhouseStorage';
import { ClickhouseIngestion } from './components/ClickhouseIngestion';
import { MSSQLStorageView } from './components/MSSQLStorageView';
import { PostgresStorageView } from './components/PostgresStorageView';
import { MariaDbStorage } from './components/MariaDbStorage';
import { MongoDbStorage } from './components/MongoDbStorage';
import { MySqlStorage } from './components/MySqlStorage';
import { OracleInstanceMemoryView } from './components/OracleInstanceMemoryView';
import { OracleAlertLogView } from './components/OracleAlertLogView';
import { OracleScheduledJobsView } from './components/OracleScheduledJobsView';
import { OracleSessionsLocksView } from './components/OracleSessionsLocksView';
import { LogMonitorView } from './components/LogMonitorView';
import DBTopologyFlow from '../topology/DBTopologyFlow';

interface ClusterOption {
  label: string;
  value: string;
  dbType?: string;
  cluster_name?: string;
  version?: string;
}

interface ClusterDropdownProps {
  clusters: ClusterOption[];
  activeCluster: string;
  onSelect: (val: string) => void;
  loading?: boolean;
}

const ClusterDropdown: React.FC<ClusterDropdownProps> = ({
  clusters,
  activeCluster,
  onSelect,
  loading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 224 });


  const updateCoords = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = Math.min(Math.max(rect.width, 224), window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left + window.scrollX, window.innerWidth - dropdownWidth - 8));
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left,
        width: dropdownWidth,
      });
    }
  }, []);

  const toggleOpen = () => {
    if (!isOpen) {
      updateCoords();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [updateCoords]);

  const selectedCluster = clusters.find((c) => c.value === activeCluster) || clusters[0];

  return (
    <div className="relative inline-block text-left flex-1 sm:flex-initial">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleOpen}
        disabled={loading || clusters.length === 0}
        className="flex items-center justify-between gap-2 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-800 dark:text-white cursor-pointer transition-colors w-full sm:w-56 shadow-sm disabled:opacity-50 h-9"
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          <DBTypeIcon dbType={selectedCluster?.dbType} className="w-4 h-4 shrink-0" />
          <span className="truncate">
            {loading ? 'Loading clusters...' : clusters.length === 0 ? 'No clusters available' : selectedCluster?.label || 'Select Cluster'}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && clusters.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
            zIndex: 99999,
          }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden py-1 max-h-60 overflow-y-auto custom-scroll animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {clusters.map((cluster) => {
            const isSelected = cluster.value === activeCluster;
            return (
              <button
                key={cluster.value}
                type="button"
                onClick={() => {
                  onSelect(cluster.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2 cursor-pointer transition-colors ${isSelected
                  ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 font-extrabold'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  <DBTypeIcon dbType={cluster.dbType} className="w-4 h-4 shrink-0" />
                  <span className="truncate">{cluster.label}</span>
                </div>
                {isSelected && <Check className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

export const DBMonitorDashboard: React.FC = () => {
  const level = useClientLevel();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clusters, setClusters] = useState<ClusterOption[]>([]);
  const [clustersLoading, setClustersLoading] = useState<boolean>(false);
  const [activeCluster, setActiveCluster] = useState<string>(
    () => searchParams.get('cluster_id') || ''
  );
  const [timeRange, setTimeRange] = useState<string>('60');
  const [activeSidebar, setActiveSidebar] = useState<string>(
    () => searchParams.get('tab') || 'topology'
  );
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false);
  const navigate = useNavigate()

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    const clusterFromUrl = searchParams.get('cluster_id');

    if (tabFromUrl && tabFromUrl !== activeSidebar) {
      setActiveSidebar(tabFromUrl);
    }
    if (clusterFromUrl && clusterFromUrl !== activeCluster) {
      setActiveCluster(clusterFromUrl);
    }
  }, [searchParams]);

  const handleSelectCluster = (val: string) => {
    setActiveCluster(val);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('cluster_id', val);
        if (!next.has('tab')) next.set('tab', activeSidebar);
        return next;
      },
      { replace: true }
    );
  };

  const handleSelectSidebar = (tabId: string) => {
    setActiveSidebar(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (activeCluster) next.set('cluster_id', activeCluster);
        next.set('tab', tabId);
        return next;
      },
      { replace: true }
    );
  };

  const fetchClusters = useCallback(async () => {
    if (level?.name?.toLowerCase() === 'global') {
      return;
    }
    setClustersLoading(true);
    try {
      const params: any = {};
      if (level?.id) {
        params.client_id = level.id;
      }
      const res = await baseapi.get('/db_monitor_dashboard/clusters', { params });
      const rawData =
        res.data?.data || res.data?.clusters || (Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(rawData) && rawData.length > 0) {
        const formatted: ClusterOption[] = rawData.map((item: any) => ({
          label: item.cluster_name || item.name || String(item.cluster_id || item.id),
          value: String(item.cluster_id || item.id || item.value),
          dbType: item.db_type || item.dbType || item.engine || item.type || 'postgres',
          cluster_name: item.cluster_name || item.name || String(item.cluster_id || item.id),
          version: item.version,
        }));
        setClusters(formatted);

        const currentUrlParams = new URLSearchParams(window.location.search);
        const currentUrlCluster = currentUrlParams.get('cluster_id');
        const matched = currentUrlCluster ? formatted.find((c) => c.value === currentUrlCluster) : undefined;
        const selectedCluster = matched ? matched.value : formatted[0].value;

        setActiveCluster(selectedCluster);

        if (currentUrlCluster !== selectedCluster) {
          currentUrlParams.set('cluster_id', selectedCluster);
          if (!currentUrlParams.has('tab')) currentUrlParams.set('tab', 'topology');
          window.history.replaceState(null, '', `?${currentUrlParams.toString()}`);
        }
      } else {
        setClusters([]);
        setActiveCluster('');
        const currentUrlParams = new URLSearchParams(window.location.search);
        currentUrlParams.delete('cluster_id');
        window.history.replaceState(null, '', `?${currentUrlParams.toString()}`);
      }
    } catch (err) {
      console.warn('Failed to fetch clusters:', err);
      setClusters([]);
      setActiveCluster('');
    } finally {
      setClustersLoading(false);
    }
  }, [level?.id, level?.name]);

  useEffect(() => {
    if (level?.name?.toLowerCase() === 'global') {
      navigate('/dashboard', { replace: true });
    } else {
      fetchClusters();
    }
  }, [fetchClusters, level?.name, navigate]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const timeOptions = [
    { label: 'Past 15 Minutes', value: '15' },
    { label: 'Past 1 Hour', value: '60' },
    { label: 'Past 4 Hours', value: '240' },
    { label: 'Past 12 Hours', value: '720' },
    { label: 'Past 24 Hours', value: '1440' },
    { label: 'Past 7 Days', value: '10080' },
  ];

  const currentClusterObj = clusters.find((c) => c.value === activeCluster);
  const currentDbType = currentClusterObj?.dbType;
  const currentClusterName = currentClusterObj?.cluster_name || currentClusterObj?.label || '';

  const isMongoDB = ['mongodb', 'mongo'].includes((currentDbType || '').toLowerCase());
  const isMySQL = ['mysql', 'mariadb'].includes((currentDbType || '').toLowerCase()) || (currentDbType || '').toLowerCase().includes('mysql');
  const isMariaDB = ['mariadb'].includes((currentDbType || '').toLowerCase()) || (currentDbType || '').toLowerCase().includes('mariadb') || (activeCluster || '').toLowerCase().includes('mariadb');
  const isPostgres = ['postgres', 'postgresql'].includes((currentDbType || '').toLowerCase()) || (currentDbType || '').toLowerCase().includes('postgres') || (activeCluster || '').toLowerCase().includes('postgres') || (activeCluster || '').toLowerCase().startsWith('pg-');
  const isOracle = ['oracle'].includes((currentDbType || '').toLowerCase()) || (activeCluster || '').toLowerCase().includes('oracle');
  const isClickHouse =
    ['clickhouse', 'click_house', 'click house'].includes((currentDbType || '').toLowerCase()) ||
    (currentDbType || '').toLowerCase().includes('clickhouse') ||
    (activeCluster || '').toLowerCase().includes('clickhouse');
  const isMSSQL =
    ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes((currentDbType || '').toLowerCase()) ||
    (currentDbType || '').toLowerCase().includes('mssql') ||
    (activeCluster || '').toLowerCase().includes('mssql');

  useEffect(() => {
    if (!currentDbType) return;
    const dbLower = currentDbType.toLowerCase();
    const isES = ['opensearch', 'elasticsearch'].includes(dbLower);
    const isMongo = ['mongodb', 'mongo'].includes(dbLower);
    const isMariaDB = ['mariadb'].includes(dbLower) || dbLower.includes('mariadb') || (activeCluster || '').toLowerCase().includes('mariadb');
    const isMSSQL = ['mssql', 'sqlserver', 'sql server', 'sql_server'].includes(dbLower) || dbLower.includes('mssql');
    const isMySQL = ['mysql'].includes(dbLower) || dbLower.includes('mysql');
    const isOracle = ['oracle'].includes(dbLower);

    if (isES && ['blocking', 'databases', 'indexes', 'storage', 'blocks', 'vacuums', 'collections', 'collections2', 'backups'].includes(activeSidebar)) {
      setActiveSidebar('overview');
    } else if (isMongo && ['esindices', 'blocks', 'vacuums', 'tempdb', 'memory', 'schedulers', 'backups'].includes(activeSidebar)) {
      setActiveSidebar('overview');
    } else if (!isES && activeSidebar === 'esindices') {
      setActiveSidebar('overview');
    } else if (!isMongo && (activeSidebar === 'collections' || activeSidebar === 'collections2')) {
      setActiveSidebar('overview');
    } else if (!isOracle && activeSidebar === 'instance_memory') {
      setActiveSidebar('overview');
    } else if (!isMSSQL && (activeSidebar === 'tempdb' || activeSidebar === 'memory' || activeSidebar === 'backups')) {
      setActiveSidebar('overview');
    } else if (!isMSSQL && !isMySQL && !isOracle && !isMariaDB && activeSidebar === 'schedulers') {
      setActiveSidebar('overview');
    }
  }, [currentDbType, activeSidebar]);

  if (level?.name?.toLowerCase() === 'global') {
    return null;
  }

  if (clustersLoading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 h-[calc(100vh-1rem)] flex flex-col overflow-hidden p-2.5 sm:p-4">
      {/* 100% Fixed Header Banner */}
      <div className="shrink-0 pb-2 border-b border-gray-200 dark:border-gray-700">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
          {/* Heading */}
          <div className="flex items-center justify-between sm:justify-start gap-3 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-orange-50 dark:bg-black/30 border border-orange-200 dark:border-orange-500/30 flex items-center justify-center text-orange-600 dark:text-white p-1.5 shrink-0">
                {clusters.length > 0 && currentDbType ? (
                  <DBTypeIcon dbType={currentDbType} className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Database className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 dark:text-orange-400" />
                )}
              </div>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white tracking-wide whitespace-nowrap">Database Monitoring</h1>
              </div>
              <div className='bg-orange-600/30 px-2 py-0.5 text-xs border border-orange-600/80 rounded-2xl text-orange-600 dark:text-orange-400 font-mono font-bold'>{currentClusterObj?.dbType} v{currentClusterObj?.version}</div>
            </div>

            {/* Mobile All Tabs Drawer Button in Title Bar */}
            <button
              onClick={() => setIsMobileDrawerOpen(true)}
              type="button"
              className="sm:hidden flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-700 text-xs font-semibold cursor-pointer transition-colors"
            >
              <span className="text-[11px] text-orange-600 dark:text-orange-400 font-bold">Menu</span>
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap w-full sm:w-auto">
              {!['topology', 'configuration',"schema"].includes(activeSidebar) && 
             <CustomDropdown
              data={timeOptions}
              selectedValue={timeRange}
              onSelect={(val) => setTimeRange(val)}
              width="w-32 sm:w-36"
              placeholder="Time Range"
              buttonHeight={9}
              disabled={activeSidebar === 'schedulers' && (isMySQL || isOracle)}
            /> }

            <ClusterDropdown
              clusters={clusters}
              activeCluster={activeCluster}
              onSelect={handleSelectCluster}
              loading={clustersLoading}
            />

            {activeSidebar !== 'topology' && (
              <button
                onClick={handleRefresh}
                className="p-2 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white border border-gray-300 dark:border-gray-700 rounded-lg transition-colors cursor-pointer shrink-0 shadow-sm"
                title="Refresh active tab"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}
           
          </div>
        </header>

        {/* Mobile Horizontal Tabs Navigation Bar */}
        {clusters.length > 0 && (
          <DBMobileTabsBar
            activeSidebar={activeSidebar}
            onSelectSidebar={handleSelectSidebar}
            dbType={currentDbType}
            onOpenDrawer={() => setIsMobileDrawerOpen(true)}
          />
        )}
      </div>

      {/* Mobile Drawer Slide-In Modal */}
      <DBMobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setIsMobileDrawerOpen(false)}
        activeSidebar={activeSidebar}
        onSelectSidebar={handleSelectSidebar}
        dbType={currentDbType}
      />

      {/* Layout Row (Desktop Fixed Sidebar + Scrollable Main Content) */}
      {!clustersLoading && clusters.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center mt-2.5 shadow-sm dark:shadow-2xl">
          <div className="p-4 rounded-full bg-amber-50 dark:bg-gray-800/80 border border-amber-200 dark:border-gray-700/60 mb-3 text-amber-500 dark:text-amber-400">
            <Database className="w-10 h-10" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">No Clusters Available</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md">
            No database clusters are configured or detected for this client environment. Please register or connect a database cluster to enable database monitoring.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row gap-3 sm:gap-4 mt-2 min-h-0 overflow-hidden">
          {/* Left Sidebar (Desktop Only) */}
          <div className={`hidden lg:block shrink-0 overflow-y-auto custom-scroll pr-1 border-r border-gray-200 dark:border-gray-800 transition-[width] duration-300 ease-in-out ${isSidebarOpen ? 'w-52' : 'w-14'}`}>
            <DBSidebar
              activeSidebar={activeSidebar}
              onSelectSidebar={handleSelectSidebar}
              isOpen={isSidebarOpen}
              onToggleOpen={() => setIsSidebarOpen(!isSidebarOpen)}
              dbType={currentDbType}
            />
          </div>

          {/* Main Content Area */}
          <main className={`flex-1 min-w-0 ${activeSidebar === 'topology' ? 'h-full flex flex-col min-h-0 overflow-hidden pr-1 sm:pr-2' : 'overflow-y-auto custom-scroll space-y-3.5 pr-1 sm:pr-2 pb-20 sm:pb-20'}`}>
            {activeSidebar === 'overview' && (
              <OverviewView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'esindices' && (
              <OpenSearchIndicesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {/* {activeSidebar === 'collections' && (
              <MongoCollectionsView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )} */}

            {activeSidebar === 'collections' && (
              <MongoCollectionsView2
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'databases' && (
              isMongoDB ? (
                <MongoWiredTigerView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (
                <DatabasesView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              )
            )}

            {activeSidebar === 'backups' && (
              <MSSQLBackupView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'tempdb' && (
              <MSSQLTempDBView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'memory' && (
              <MSSQLMemoryClerksView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'instance_memory' && (
              <OracleInstanceMemoryView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'schedulers' && (
              isOracle ? (
                <OracleScheduledJobsView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMySQL ? (
                <MySQLScheduledJobsView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (
                <MSSQLSchedulersView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              )
            )}

            {activeSidebar === 'file_io' && (
              isMongoDB ? (
                <MongoFileIOView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (isPostgres || isMariaDB) ? (
                <PostgresFileIOView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isClickHouse ? (
                <ClickhouseFileIOView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMySQL ? (
                <MySQLFileIOView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (
                <MSSQLFileIOView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              )
            )}

            {activeSidebar === 'indexes' && (
              <IndexesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {(activeSidebar === 'storage' || activeSidebar === 'clickhousestorage' || activeSidebar === 'clickhouse_storage' || activeSidebar === 'postgresstorage' || activeSidebar === 'postgres_storage' || activeSidebar === 'mongostorage' || activeSidebar === 'mongodbstorage' || activeSidebar === 'mongodb_storage' || activeSidebar === 'mariadbstorage' || activeSidebar === 'mariadb_storage' || activeSidebar === 'mysqlstorage' || activeSidebar === 'mysql_storage' || activeSidebar === 'blocks' || activeSidebar === 'vacuums') && (
              isClickHouse ? (
                <ClickhouseStorage
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMSSQL ? (
                <MSSQLStorageView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isPostgres ? (
                <PostgresStorageView
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMariaDB ? (
                <MariaDbStorage
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMongoDB ? (
                <MongoDbStorage
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : isMySQL ? (
                <MySqlStorage
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (
                <StorageMaintenanceTab
                  activeCluster={activeCluster}
                  timeRange={timeRange}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              )
            )}

            {(activeSidebar === 'ingestion' || activeSidebar === 'clickhouseingestion' || activeSidebar === 'clickhouse_ingestion') && (
              <ClickhouseIngestion
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'blocking' && (
              <QueriesView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'users' && (
              <UsersLoginsView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'user_queries' && (
              <UserQueryStatisticsView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'schema' && (
              <SchemaView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {activeSidebar === 'calling' && <CallingServicesView />}

            {activeSidebar === 'topology' && (
              <div className="w-full h-full flex-1 min-h-0 pb-20">
                <DBTopologyFlow clusterId={activeCluster} />
              </div>
            )}

            {activeSidebar === 'metrics' && (
              <MetricsTab
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'connections' && !isOracle && (
              <ConnectionsTab
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {(activeSidebar === 'sessions_locks' || (activeSidebar === 'connections' && isOracle)) && (
              <OracleSessionsLocksView
                activeCluster={activeCluster}
                timeRange={timeRange}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}

            {activeSidebar === 'health' && (
              <HealthTab
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
              />
            )}

            {(activeSidebar === 'logs' || activeSidebar === 'log_monitor' || activeSidebar === 'alert_log') && (
              isOracle ? (
                <OracleAlertLogView
                  activeCluster={activeCluster}
                  clusterName={currentClusterName}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                />
              ) : (
                <LogMonitorView
                  activeCluster={activeCluster}
                  clusterName={currentClusterName}
                  clientId={level?.id}
                  refreshKey={refreshKey}
                  dbType={currentDbType}
                  timeRange={timeRange}
                />
              )
            )}

            {activeSidebar === 'configuration' && (
              <ConfigurationView
                activeCluster={activeCluster}
                clientId={level?.id}
                refreshKey={refreshKey}
                dbType={currentDbType}
              />
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default DBMonitorDashboard;
